import React from 'react';
import { Box, Collapse, Typography, useTheme } from '@mui/material';
import { IconChevronRight } from '@tabler/icons-react';

/**
 * Group container (DESIGN_SYSTEM.md section 8.13, #1863). The one sanctioned
 * tier beneath section containers: a recessed box that binds several cards
 * into one purpose group on a page (the admin console's Access, Messaging and
 * Exports groups). It is a tier, not a card: recessed surface, hairline
 * border, page-shell radius, and no dot or count of its own.
 *
 * Header: a real `h2` (the page hero is the `h1`) wrapping a toggle button
 * (chevron + `aria-expanded`), then a one-sentence subtitle that stays visible
 * whether the group is open or collapsed. Children collapse via MUI Collapse
 * WITHOUT `unmountOnExit`: the cards inside fetch on mount and hold form
 * state, so hiding them must not throw that away (unlike SectionContainer's
 * own collapsible mode, which unmounts). `defaultCollapsed` is local state
 * only; nothing persists across reloads.
 *
 * Groups never nest in groups and never sit inside a card (section 12, 11).
 */
const GroupContainer = ({
  title,
  subtitle,
  defaultCollapsed = false,
  children,
  sx,
  ...rest
}) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
  const contentId = React.useId();
  const subtitleId = React.useId();

  return (
    <Box
      component="section"
      sx={{
        backgroundColor: palette.surface.recessed,
        border: `1px solid ${palette.divider}`,
        borderRadius: `${ds.radius.shell}px`,
        padding: `${ds.space[4]}px`,
        transition: `background-color ${ds.motion.base} ${ds.motion.easeOut}, border-color ${ds.motion.base} ${ds.motion.easeOut}`,
        ...sx,
      }}
      {...rest}
    >
      <Box component="h2" sx={{ m: 0 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={contentId}
          aria-describedby={subtitle ? subtitleId : undefined}
          sx={{
            // Reset native button chrome so the header reads as a heading
            // that happens to be clickable (mirrors SectionContainer).
            display: 'flex',
            alignItems: 'center',
            gap: `${ds.space[2]}px`,
            width: '100%',
            appearance: 'none',
            background: 'transparent',
            border: 0,
            padding: 0,
            font: 'inherit',
            color: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
            '&:focus-visible': {
              outline: `1.5px solid ${palette.interactive}`,
              outlineOffset: '2px',
            },
          }}
        >
          <IconChevronRight
            size={16}
            aria-hidden
            style={{
              transform: collapsed ? 'none' : 'rotate(90deg)',
              transition: `transform ${ds.motion.fast} ${ds.motion.easeOut}`,
              flexShrink: 0,
              color: palette.text.dim,
              // Decorative: clicks fall through to the toggle button (#1272).
              pointerEvents: 'none',
            }}
          />
          <Box
            component="span"
            sx={{
              fontSize: `${ds.font.metric}px`,
              fontWeight: ds.font.weightMedium,
              color: palette.text.primary,
              minWidth: 0,
            }}
          >
            {title}
          </Box>
        </Box>
      </Box>
      {subtitle && (
        <Typography
          id={subtitleId}
          sx={{
            mt: `${ds.space[1]}px`,
            fontSize: `${ds.font.dim}px`,
            color: palette.text.secondary,
          }}
        >
          {subtitle}
        </Typography>
      )}
      <Collapse in={!collapsed} id={contentId}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '13px', mt: `${ds.space[4]}px` }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export default GroupContainer;
